import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle auth errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// API helper functions
export const productsApi = {
  getAll: (params) => api.get('/products', { params }),
  getOne: (id) => api.get(`/products/${id}`),
};

export const categoriesApi = {
  getAll: () => api.get('/categories'),
};

export const ordersApi = {
  getAll: () => api.get('/orders'),
  getOne: (id) => api.get(`/orders/${id}`),
};

export const depositsApi = {
  getAll: () => api.get('/deposits'),
  create: (data) => api.post('/deposits', data),
};

export const transfersApi = {
  getAll: () => api.get('/transfers'),
  create: (data) => api.post('/transfers', data),
};

export const settingsApi = {
  get: () => api.get('/settings'),
};

export const disputesApi = {
  getAll: () => api.get('/disputes'),
  getOne: (id) => api.get(`/disputes/${id}`),
  getByOrder: (orderId) => api.get(`/disputes/by-order/${orderId}`),
  create: (data) => api.post('/disputes', data),
  addMessage: (id, content) => api.post(`/disputes/${id}/messages`, { content }),
};

// Admin API
export const adminApi = {
  getStats: () => api.get('/admin/stats'),
  
  // Users
  getUsers: (params) => api.get('/admin/users', { params }),
  toggleUser: (id) => api.post(`/admin/users/${id}/toggle`),
  adjustBalance: (id, amount) => api.post(`/admin/users/${id}/balance?amount=${amount}`),
  makeAdmin: (id) => api.post(`/admin/users/${id}/make-admin`),
  resetPassword: (id, newPassword) => api.post(`/admin/users/${id}/reset-password`, { new_password: newPassword }),
  
  // Products
  createProduct: (data) => api.post('/admin/products', data),
  updateProduct: (id, data) => api.put(`/admin/products/${id}`, data),
  deleteProduct: (id) => api.delete(`/admin/products/${id}`),
  
  // Categories
  createCategory: (data) => api.post('/admin/categories', data),
  updateCategory: (id, data) => api.put(`/admin/categories/${id}`, data),
  deleteCategory: (id) => api.delete(`/admin/categories/${id}`),
  
  // Licenses
  createLicense: (data) => api.post('/admin/licenses', data),
  createLicensesBulk: (productId, licenses) => api.post(`/admin/licenses/bulk?product_id=${productId}`, licenses),
  getProductLicenses: (productId) => api.get(`/admin/licenses/${productId}`),
  
  // Deposits
  getDeposits: (status) => api.get('/admin/deposits', { params: { status } }),
  approveDeposit: (id, amount, note) => api.post(`/admin/deposits/${id}/approve`, null, { params: { amount, note } }),
  rejectDeposit: (id, note) => api.post(`/admin/deposits/${id}/reject`, null, { params: { note } }),
  
  // Orders
  getOrders: (params) => api.get('/admin/orders', { params }),
  getOrder: (id) => api.get(`/admin/orders/${id}`),
  
  // Disputes
  getDisputes: (status) => api.get('/admin/disputes', { params: { status } }),
  updateDisputeStatus: (id, status) => api.post(`/admin/disputes/${id}/status?status=${status}`),
  addDisputeMessage: (id, content) => api.post(`/admin/disputes/${id}/messages`, { content }),
  refundDispute: (id) => api.post(`/admin/disputes/${id}/refund`),
  deleteDispute: (id) => api.delete(`/admin/disputes/${id}`),
  deleteAllDisputes: () => api.delete('/admin/disputes'),
  
  // Chat
  getChatUsers: () => api.get('/admin/chat/users'),
  getChatMessages: (userId) => api.get(`/admin/chat/messages/${userId}`),
  sendChatMessage: (userId, content) => api.post(`/admin/chat/messages/${userId}`, { content }),
  deleteUserChat: (userId) => api.delete(`/admin/chat/user/${userId}`),
  deleteAllChat: () => api.delete('/admin/chat'),
  
  // Notifications & Broadcasts
  sendNotification: (data) => api.post('/admin/notifications', data),
  getBroadcasts: () => api.get('/admin/broadcasts'),
  sendBroadcast: (data) => api.post('/admin/broadcast', data),
  deleteBroadcast: (id) => api.delete(`/admin/broadcasts/${id}`),
  deleteAllBroadcasts: () => api.delete('/admin/broadcasts'),
  
  // Settings
  getSettings: () => api.get('/admin/settings'),
  updateSettings: (data) => api.put('/admin/settings', data),
  
  // Resellers
  getResellers: () => api.get('/admin/resellers'),
  toggleReseller: (id) => api.post(`/admin/resellers/${id}/toggle`),
  
  // Transactions & Refunds (Audit Trail)
  getTransactions: (params) => api.get('/admin/transactions', { params }),
  getRefunds: (params) => api.get('/admin/refunds', { params }),
};

// Reseller API
export const resellerApi = {
  register: (data) => api.post('/reseller/register', data),
  getPanel: () => api.get('/reseller/panel'),
  updateSettings: (data) => api.put('/reseller/settings', data),
  getProducts: () => api.get('/reseller/products'),
  setProductPrice: (productId, price) => api.post('/reseller/products/price', { product_id: productId, reseller_price: price }),
  getOrders: () => api.get('/reseller/orders'),
  getStats: () => api.get('/reseller/stats'),
};

// Push notifications API
export const pushApi = {
  subscribe: (subscription) => api.post('/push/subscribe', subscription),
  unsubscribe: () => api.delete('/push/unsubscribe'),
  getVapidKey: () => api.get('/push/vapid-key'),
};

// Upload helper
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};

// Upload product image (admin only)
export const uploadProductImage = async (file) => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await api.post('/upload/product-image', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return response.data;
};
